import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from './useAuth';
import type { PersistedGraph } from '../domains/compass/models/KnowledgeGraph';

/**
 * Load the full folder knowledge graph for the Compass Explorer.
 *
 * The explorer loads the graph once and keeps it in memory, then
 * `sliceForFocus` produces a subgraph for each {focus, zoomLevel}
 * change without re-hitting the server.
 *
 * When the graph is very large (10k+ resources) we'll want to swap in
 * a lazy loader that fetches only the focused VPC's subgraph — tracked
 * in the plan doc under "Risks: full-folder graph load size."
 */

export interface CompassGraphResponse {
  graphId: string;
  version: number;
  updatedAt: string;
  nodes: PersistedGraph['nodes'];
  edges: PersistedGraph['edges'];
  documents: PersistedGraph['documents'];
}

export function useCompassGraph(
  folderId: string | undefined,
  orgId: string | undefined,
  queryEnabled = true,
) {
  const fetcher = useAuthenticatedFetch();
  const enabled = queryEnabled && Boolean(folderId) && Boolean(orgId);

  return useQuery({
    queryKey: ['compass', 'graph', folderId, orgId],
    enabled,
    queryFn: async (): Promise<CompassGraphResponse> => {
      const url = `/api/compass/folders/${folderId}/graph?orgId=${encodeURIComponent(orgId ?? '')}`;
      const r = await fetcher(url);
      if (!r.ok) throw new Error(`Failed to load Compass graph: ${r.status}`);
      return (await r.json()) as CompassGraphResponse;
    },
    staleTime: 30_000,
  });
}
